import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import ActivitySection from './components/ActivitySection';
import SessionsSection from './components/SessionsSection';
import BehaviorSection from './components/BehaviorSection';
import MonthlyHistory from './components/MonthlyHistory';
import UpcomingMatchesSection from './components/UpcomingMatchesSection';
import AvatarImg from '../../components/AvatarImg';

const SERVER_COLORS = {
  MENA: '#f97316', FR: '#3b82f6', IT: '#22c55e', DE: '#a855f7',
};
const LEVEL_CONFIG = {
  Junior: { color: '#6b7280', bg: 'rgba(107,114,128,0.12)', border: 'rgba(107,114,128,0.3)' },
  Rising: { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' },
  Senior: { color: '#FF6B00', bg: 'rgba(255,107,0,0.12)', border: 'rgba(255,107,0,0.3)' },
  Elite:  { color: '#FFD700', bg: 'rgba(255,215,0,0.12)', border: 'rgba(255,215,0,0.3)' },
};
const FRENCH_MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const formatPeriodFR = (period) => {
  if (!period) return '';
  const parts = period?.split('-');
  if (parts?.length >= 2) {
    const year = parts?.[0];
    const month = parseInt(parts?.[1], 10);
    if (month >= 1 && month <= 12) return `${FRENCH_MONTHS[month - 1]} ${year}`;
  }
  return period;
};
const fmtCount = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
};
const fmtDiamonds = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(Math.round(n));
};
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

const CreatorProfileDetail = () => {
  const { t } = useTranslation();
  const { tiktok_id } = useParams();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [creator, setCreator] = useState(null);
  const [creatorLoading, setCreatorLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [currentMonthLoading, setCurrentMonthLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [acConfig, setAcConfig] = useState([]);
  const [whatsappEdit, setWhatsappEdit] = useState(false);
  const [whatsappValue, setWhatsappValue] = useState('');
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');
  const [violations, setViolations] = useState([]);
  const [violationsLoading, setViolationsLoading] = useState(true);
  const [hasActiveViolation, setHasActiveViolation] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session.user.email);
    });
  }, []);

  useEffect(() => {
    if (!tiktok_id) return;
    const fetchCreator = async () => {
      setCreatorLoading(true);
      const { data } = await supabase.from('creators').select('tiktok_id, username, display_name, whatsapp, server, level, contract_start, contract_end, tier_last_month, last_live_date, group_name, manager_id, agency_status, departure_date, peak_tier, peak_diamonds, peak_period, tiktok_avatar_url, tiktok_avatar_thumb, tiktok_follower_count, tiktok_likes_count, tiktok_video_count, tiktok_display_name, region').eq('tiktok_id', tiktok_id).single();
      if (data) { setCreator(data); setWhatsappValue(data.whatsapp || ''); }
      setCreatorLoading(false);
    };
    const fetchCurrentMonth = async () => {
      setCurrentMonthLoading(true);
      const { data } = await supabase.from('creator_current_month').select('*, cashback_amount, cashback_pct').eq('tiktok_id', tiktok_id).single();
      if (data) setCurrentMonth(data);
      setCurrentMonthLoading(false);
    };
    const fetchMonthlyHistory = async () => {
      setMonthlyLoading(true);
      const { data } = await supabase.from('creator_monthly_history').select('period, diamonds, live_days, live_hours, tier_p, activeness_level, activeness_bonus, rankup_bonus, incremental_bonus, total_bonus, charge, cashback, net_agency, is_violative, is_estimated').eq('tiktok_id', tiktok_id).order('period', { ascending: false });
      if (data) setMonthlyData(data);
      setMonthlyLoading(false);
    };
    const fetchViolations = async () => {
      setViolationsLoading(true);
      const { data } = await supabase.from('violations').select('id, violation_type, penalty_status, appeal_status, punishment_time, recovery_time').eq('tiktok_id', tiktok_id).order('punishment_time', { ascending: false });
      const vList = data || [];
      setViolations(vList);
      setHasActiveViolation(vList.some(v => v.penalty_status === 'In effect'));
      setViolationsLoading(false);
    };
    fetchCreator();
    fetchCurrentMonth();
    fetchMonthlyHistory();
    fetchViolations();
  }, [tiktok_id]);

  useEffect(() => {
    if (!creator?.server) return;
    supabase.from('activeness_config').select('level, min_live_days, min_live_hours, min_diamonds, bonus_ratio').eq('server', creator.server).order('level', { ascending: true }).then(({ data }) => { if (data) setAcConfig(data); });
  }, [creator?.server]);

  const handleWhatsappSave = async () => {
    const val = whatsappValue?.trim();
    if (val && !/^\+[0-9]{8,15}$/.test(val)) { setWhatsappError(t('admin.creators.invalidFormat', 'Format invalide. Doit commencer par + et contenir 8-15 chiffres.')); return; }
    setWhatsappSaving(true); setWhatsappError('');
    const { error } = await supabase.from('creators').update({ whatsapp: val || null }).eq('tiktok_id', tiktok_id);
    if (!error) { setCreator(prev => ({ ...prev, whatsapp: val || null })); setWhatsappEdit(false); }
    else setWhatsappError(t('admin.creators.saveError', 'Erreur lors de la sauvegarde.'));
    setWhatsappSaving(false);
  };

  const activeStatus = currentMonth?.is_active === true;
  const serverColor = SERVER_COLORS[creator?.server] || '#E5202A';
  const today = new Date(); today.setHours(0,0,0,0);
  const contractStart = creator?.contract_start ? new Date(creator.contract_start) : null;
  const contractEnd = creator?.contract_end ? new Date(creator.contract_end) : null;
  const isExpired = contractEnd && contractEnd < today;
  const levelCfg = creator?.level ? LEVEL_CONFIG[creator.level] : null;
  const lastLiveFormatted = formatDate(currentMonth?.last_live_date);

  return (
    <div style={{ display:'flex', minHeight:'100vh', backgroundColor:'var(--color-background)', fontFamily:'Inter, sans-serif' }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} userEmail={userEmail} />
      </div>
      <main className="pb-16 md:pb-0" style={{ flex:1, overflowY:'auto', minWidth:0 }}>
        <AdminMobileHeader title={t('admin.creators.title', 'Créateurs')} />
        {/* Header */}
        <div style={{ position:'sticky', top:0, zIndex:10, padding:'16px 24px', borderBottom:'1px solid #1f1f1f', backgroundColor:'var(--color-background)' }}>
          <button onClick={() => navigate('/admin/creators')} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:'rgba(255,255,255,0.4)', marginBottom:'12px', background:'none', border:'none', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('admin.creators.backToCreators', 'Retour aux créateurs')}
          </button>

          {creatorLoading ? (
            <div style={{ animation:'pulse 2s infinite' }}>
              <div style={{ height:'28px', width:'200px', borderRadius:'6px', backgroundColor:'#1f1f1f', marginBottom:'8px' }} />
            </div>
          ) : (
            <>
              {/* Profile header with avatar */}
              <div style={{ display:'flex', gap:'16px', alignItems:'flex-start', marginBottom:'8px' }}>
                {/* Avatar */}
                <div style={{ flexShrink:0 }}>
                  <AvatarImg
                    src={creator?.tiktok_avatar_url || creator?.tiktok_avatar_thumb}
                    username={creator?.username}
                    style={{ width:64, height:64, borderRadius:'50%', objectFit:'cover', border:`2px solid ${serverColor}40` }}
                  />
                </div>

                {/* Info block */}
                <div style={{ flex:1, minWidth:0 }}>
                  {/* Username + tiktok_id */}
                  <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'4px' }}>
                    <h1 style={{ fontSize:'22px', fontWeight:'500', color:'white', fontFamily:'Sora, sans-serif', margin:0 }}>
                      {creator?.username || creator?.tiktok_id}
                    </h1>
                    {hasActiveViolation && (
                      <span style={{ fontSize:'13px', fontWeight:'600', color:'#ef4444' }}>⚠️ {t('admin.creators.activeViolation', 'Violation active')}</span>
                    )}
                    <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.35)', backgroundColor:'rgba(255,255,255,0.05)', padding:'2px 8px', borderRadius:'4px' }}>
                      @{creator?.tiktok_id}
                    </span>
                  </div>
                  {/* Display name */}
                  {creator?.display_name && (
                    <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', margin:'0 0 6px 0' }}>{creator.display_name}</p>
                  )}
                  {/* TikTok stats */}
                  {creator?.tiktok_follower_count != null && (
                    <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'8px' }}>
                      <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)' }}>
                        <span style={{ fontWeight:'600', color:'rgba(255,255,255,0.8)' }}>{fmtCount(creator.tiktok_follower_count)}</span> followers
                      </span>
                      {creator?.tiktok_likes_count > 0 && (
                        <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)' }}>
                          <span style={{ fontWeight:'600', color:'rgba(255,255,255,0.8)' }}>{fmtCount(creator.tiktok_likes_count)}</span> likes
                        </span>
                      )}
                      {creator?.tiktok_video_count > 0 && (
                        <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)' }}>
                          <span style={{ fontWeight:'600', color:'rgba(255,255,255,0.8)' }}>{fmtCount(creator.tiktok_video_count)}</span> vidéos
                        </span>
                      )}
                    </div>
                  )}
                  {/* Row 1: server, level, peak */}
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'6px' }}>
                    {creator?.server && (
                      <span style={{ fontSize:'11px', fontWeight:'500', padding:'3px 8px', borderRadius:'6px', backgroundColor:`${serverColor}18`, color:serverColor, border:`1px solid ${serverColor}30` }}>
                        {creator.server}
                      </span>
                    )}
                    {creator?.level && levelCfg && (
                      <span style={{ fontSize:'11px', fontWeight:'500', padding:'3px 8px', borderRadius:'6px', backgroundColor:levelCfg.bg, color:levelCfg.color, border:`1px solid ${levelCfg.border}` }}>
                        {creator.level}
                      </span>
                    )}
                    {creator?.region && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: '#ffffff10', color: 'rgba(255,255,255,0.4)' }}>
                        {creator.region}
                      </span>
                    )}
                    {creator?.peak_diamonds > 0 && (
                      <span style={{ fontSize:'11px', color:'rgba(255,215,0,0.7)' }}>
                        🏆 {t('admin.creators.peak', 'Peak')}: {fmtDiamonds(creator?.peak_diamonds)} · {formatPeriodFR(creator?.peak_period)}
                      </span>
                    )}
                    {currentMonth?.manager_name && (
                      <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)' }}>👤 {currentMonth.manager_name}</span>
                    )}
                  </div>
                </div>
              </div>
              {/* Row 2: actif, dernier live, whatsapp, contrat */}
              <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'6px' }}>
                {activeStatus ? (
                  <span style={{ fontSize:'11px', fontWeight:'500', padding:'3px 8px', borderRadius:'20px', backgroundColor:'rgba(34,197,94,0.12)', color:'#22c55e', border:'1px solid rgba(34,197,94,0.25)' }}>{t('admin.creators.activeThisMonth', 'Actif ce mois')}</span>
                ) : (
                  <span style={{ fontSize:'11px', fontWeight:'500', padding:'3px 8px', borderRadius:'20px', backgroundColor:'rgba(239,68,68,0.12)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.25)' }}>{t('admin.creators.inactiveThisMonth', 'Inactif ce mois')}</span>
                )}
                {lastLiveFormatted && (
                  <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)' }}>
                    {t('admin.creators.lastLive', 'Dernier live')} : <span style={{ color:'rgba(255,255,255,0.7)' }}>{lastLiveFormatted}</span>
                  </span>
                )}
                {/* WhatsApp */}
                {whatsappEdit ? (
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                    <input type="text" value={whatsappValue} onChange={e => { setWhatsappValue(e.target.value); setWhatsappError(''); }} placeholder="+33612345678"
                      style={{ padding:'4px 10px', borderRadius:'6px', fontSize: '16px', color:'white', backgroundColor:'#111', border:`1px solid ${whatsappError?'#ef4444':'#1f1f1f'}`, width:'100%', maxWidth:'200px', minWidth:'120px', outline:'none' }} autoFocus />
                    <button onClick={handleWhatsappSave} disabled={whatsappSaving} style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'600', backgroundColor:'#25D366', color:'white', border:'none', cursor:'pointer' }}>
                      {whatsappSaving ? '...' : t('admin.creators.save', 'Sauvegarder')}
                    </button>
                    <button onClick={() => { setWhatsappEdit(false); setWhatsappValue(creator?.whatsapp||''); setWhatsappError(''); }} style={{ padding:'4px 8px', borderRadius:'6px', fontSize:'11px', backgroundColor:'#1f1f1f', color:'rgba(255,255,255,0.5)', border:'none', cursor:'pointer' }}>
                      {t('admin.creators.cancel', 'Annuler')}
                    </button>
                    {whatsappError && <span style={{ fontSize:'11px', color:'#ef4444' }}>{whatsappError}</span>}
                  </div>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    {creator?.whatsapp ? (
                      <a href={`https://wa.me/${creator.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                        style={{ fontSize:'11px', fontWeight:'500', padding:'3px 10px', borderRadius:'20px', backgroundColor:'rgba(34,197,94,0.15)', color:'#22c55e', border:'1px solid rgba(34,197,94,0.3)', textDecoration:'none' }}>
                        {creator.whatsapp}
                      </a>
                    ) : (
                      <span style={{ fontSize:'11px', fontWeight:'500', padding:'3px 10px', borderRadius:'20px', backgroundColor:'rgba(249,115,22,0.15)', color:'#f97316', border:'1px solid rgba(249,115,22,0.3)' }}>
                        {t('admin.creators.noNumber', 'Aucun numéro')}
                      </span>
                    )}
                    <button onClick={() => setWhatsappEdit(true)} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.4)', padding:'2px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  </div>
                )}
                <span style={{ fontSize:'11px', color: isExpired ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>
                  📅 {contractStart ? contractStart.toLocaleDateString('fr-FR') : '—'} → {contractEnd ? contractEnd.toLocaleDateString('fr-FR') : '—'}
                  {isExpired && <span style={{ marginLeft:'4px', fontWeight:'600' }}>({t('admin.creators.expired', 'Expiré')})</span>}
                </span>
              </div>

              {/* Agency status banner */}
              {creator?.agency_status === 'left_agency' && (
                <div style={{ marginTop:'8px', padding:'10px 14px', borderRadius:'8px', backgroundColor:'rgba(107,114,128,0.08)', border:'1px solid rgba(107,114,128,0.25)' }}>
                  <p style={{ fontSize:'13px', fontWeight:'600', color:'#9ca3af', margin:0 }}>
                    ⚠️ {t('admin.creators.leftAgencyBanner', 'Ce créateur a quitté l\'agence')}
                    {creator?.departure_date && <span style={{ marginLeft:'8px', fontWeight:'400', fontSize:'12px' }}>· {t('admin.creators.departure', 'Départ')}: {creator.departure_date}</span>}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Content */}
        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'32px' }}>
          <ActivitySection
            currentMonth={currentMonth}
            tiktok_id={tiktok_id}
            server={creator?.server}
            loading={currentMonthLoading}
            acConfig={acConfig}
          />
          {/* Matchs à venir */}
          <UpcomingMatchesSection tiktokId={tiktok_id} />

          {/* Sessions LIVE */}
          <div>
            <h2 style={{ fontSize:'13px', fontWeight:'600', color:'rgba(255,255,255,0.4)', fontFamily:'Sora, sans-serif', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'12px' }}>
              {t('admin.creators.sessionsLive', 'Sessions LIVE')}
            </h2>
            <SessionsSection
              tiktokId={tiktok_id}
              server={creator?.server}
              period={currentMonth?.period}
            />
          </div>
          <div>
            <h2 style={{ fontSize:'13px', fontWeight:'600', color:'rgba(255,255,255,0.4)', fontFamily:'Sora, sans-serif', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'12px' }}>
              {t('admin.creators.behaviorLive', 'Comportement LIVE')}
            </h2>
            <BehaviorSection tiktokId={tiktok_id} />
          </div>
          <MonthlyHistory monthlyData={monthlyData} loading={monthlyLoading} />

          {/* Violations Section */}
          <div>
            <h2 style={{ fontSize:'13px', fontWeight:'600', color:'rgba(255,255,255,0.4)', fontFamily:'Sora, sans-serif', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'12px' }}>
              {t('admin.creators.violations', 'Violations')}
            </h2>
            <div style={{ borderRadius:'12px', border:'1px solid #1f1f1f', backgroundColor:'#111111', overflow:'hidden' }}>
              {violationsLoading ? (
                <div style={{ padding:'16px' }}>
                  {[1,2,3].map(i => <div key={i} style={{ height:'32px', borderRadius:'6px', backgroundColor:'#1a1a1a', marginBottom:'8px', animation:'pulse 2s infinite' }} />)}
                </div>
              ) : violations.length === 0 ? (
                <p style={{ padding:'24px', textAlign:'center', fontSize:'12px', color:'rgba(255,255,255,0.3)', fontFamily:'Inter, sans-serif' }}>
                  {t('admin.creators.noViolation', 'Aucune violation enregistrée')}
                </p>
              ) : violations.map((v, i) => {
                const penaltyConfig = { 'In effect': { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' }, 'Ended': { bg: 'rgba(249,115,22,0.12)', color: '#f97316' }, 'Withdrawn': { bg: 'rgba(34,197,94,0.12)', color: '#22c55e' } };
                const appealConfig = { 'Not appealed': { bg: 'rgba(249,115,22,0.12)', color: '#f97316' }, 'Not approved': { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' }, 'Not approved, open to inquiry': { bg: 'rgba(220,38,38,0.12)', color: '#dc2626' }, 'Ticket opened': { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' }, 'Approved': { bg: 'rgba(34,197,94,0.12)', color: '#22c55e' } };
                const pc = penaltyConfig[v.penalty_status] || { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' };
                const ac = appealConfig[v.appeal_status] || { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' };
                const fmtD = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '-';
                return (
                  <div key={v.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', borderBottom: i < violations.length - 1 ? '1px solid #1a1a1a' : 'none', gap:'8px' }}>
                    <div style={{ minWidth:0, flex:1 }}>
                      <p style={{ fontSize:'13px', color:'white', fontFamily:'Inter, sans-serif', margin:0 }}>{v.violation_type}</p>
                      <p style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)', margin:'2px 0 0', fontFamily:'Inter, sans-serif' }}>{fmtD(v.punishment_time)} → {fmtD(v.recovery_time)}</p>
                    </div>
                    <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                      <span style={{ fontSize:'11px', fontWeight:'600', padding:'2px 8px', borderRadius:'20px', backgroundColor:pc.bg, color:pc.color, fontFamily:'Inter, sans-serif' }}>{v.penalty_status}</span>
                      <span style={{ fontSize:'11px', fontWeight:'600', padding:'2px 8px', borderRadius:'20px', backgroundColor:ac.bg, color:ac.color, fontFamily:'Inter, sans-serif' }}>{v.appeal_status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default CreatorProfileDetail;
